import { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Space, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { Crud, CrudMode, Table, type CrudProps, type TableComponent, type TableProps, type ValueLists } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType } from '@pmp/api';
import { getColumnsByEntityType } from '../../CrudTable/utils';
import { ChangeLogEntry, ChangeLogFieldChange, ChangeLogTarget, ChangeLogValue } from '../interfaces';
import { useValueListsStore } from '@pmp/stores';
import { columns, FIELD_LIST_NAME, getExpandedColumns, getOriginList, getOriginTagMappings, ORIGIN_LIST_NAME } from '../tableConfig';
import { useChangeDetail } from '../useChangeDetail';
import { useChangeLogEntries } from '../useChangeLogEntries';
import { REFERENCE_LIST_NAMES, useReferenceLists } from '../useReferenceLists';
import { getSubTables, RECORD_EXTRA_COLUMNS, RECORD_SECTION_TITLES, SUB_TABLE_LIST_NAMES } from '../subTables';
import styles from '../styles.module.css';

const { Text } = Typography;

const rowKey = 'id';

const DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

type ChangeLogDrawerProps = {
  target: ChangeLogTarget | null;
  onClose: () => void;
};

/**
 * Read-only change log for one firm or intermediary, opened from the list's row action.
 *
 * One row per change, expanding to what each field held before and after it. Rendered through Crud in
 * VIEW mode so it inherits the drawer chrome every other View screen uses: title, close on the right,
 * a Close-only footer.
 */
export const ChangeLogDrawer = memo(({ target, onClose }: ChangeLogDrawerProps): ReactElement | null => {
  const { t } = useTranslation();
  const { entries, isFetching, failed } = useChangeLogEntries(target);
  const { valueLists: sharedValueLists, getValueLists } = useValueListsStore();
  const { detail, loadDetail, reset } = useChangeDetail(target);
  const referenceLists = useReferenceLists(target, detail);
  const referenceListNames = useMemo(() => REFERENCE_LIST_NAMES[target?.entityType as ActionEntityType] ?? {}, [target]);
  const tableRef = useRef<TableComponent | null>(null);
  const [filtersActive, setFiltersActive] = useState(false);
  const originList = useMemo(() => getOriginList(t, target?.entityType ?? ''), [t, target]);

  // The table stores its filters and its search under its id, which is one id for every record, so
  // whatever was left on the last record would come back on the next one and silently hide its log.
  const openClean = useCallback((table: TableComponent | null) => {
    if (!table) return;

    table.resetFilters();
    table.setSearch('');
    setFiltersActive(false);
  }, []);

  // The drawer mounts its table after this component's effects run, so the table is only reachable
  // here, when it attaches itself. The Table rebuilds its handle on every render and so calls this
  // on every render: without the sentinel, clearing the filters would re-render and never settle.
  const cleanedFor = useRef<ChangeLogTarget | null>(null);
  const attachTable = useCallback(
    (table: TableComponent | null) => {
      tableRef.current = table;
      if (!table || cleanedFor.current === target) return;

      cleanedFor.current = target;
      openClean(table);
    },
    [target, openClean],
  );

  // Each record gets its own detail cache, so reopening the drawer on another record fetches again.
  useEffect(() => reset, [target, reset]);

  const entityColumns = useMemo(
    () => [
      ...(getColumnsByEntityType(target?.entityType ?? null) ?? []),
      ...(RECORD_EXTRA_COLUMNS[target?.entityType as ActionEntityType] ?? []),
    ],
    [target],
  );

  // Every column the detail renders reads its labels off the shared value lists, or a cell shows the
  // stored code where the change request screen shows a name.
  //
  // Only the lists the options endpoint knows: a column can name a list the app builds itself, such as
  // the qualified domains a firm's change request suggests, and asking the backend for one is a 400.
  useEffect(() => {
    const known = new Set<string>(Object.values(FieldType));
    const listNames = [
      ...new Set([
        ...SUB_TABLE_LIST_NAMES,
        ...entityColumns.map(column => column.options?.listName),
        ...Object.values(referenceListNames),
      ]),
    ].filter(
      (listName): listName is FieldType => !!listName && known.has(listName),
    );
    getValueLists({ fetchIfMissing: listNames });
  }, [getValueLists, entityColumns, referenceListNames]);

  // A field of a collection travels as `<collection>.<column>`; anything else is the record's own. A
  // field the grid shows under another key, as a product's `baseRate` under `baseRateName`, is found by
  // the property it reads, preferring the column the grid shows over its hidden id twin.
  const findColumn = useCallback(
    (field: string) => {
      const [collection, column] = field.split('.');
      const subTable = getSubTables(target?.entityType)[collection];
      if (subTable) return subTable.columns.find(candidate => String(candidate.key) === column);

      const byProperty = entityColumns.filter(candidate => candidate.dataIndex === field && candidate.options?.listName);

      return (
        entityColumns.find(candidate => candidate.key === field) ??
        byProperty.find(candidate => !candidate.options?.hide) ??
        byProperty[0]
      );
    },
    [target, entityColumns],
  );

  // A log entry lists the fields of a change request row, so the change request's own columns are
  // where their names come from: the two screens then call the same field the same thing, and a field
  // added to the entity is named here without anyone remembering to add it.
  const columnLabel = useCallback(
    (field: string) => {
      const title = findColumn(field)?.title;
      if (typeof title === 'string') return t(title);

      const [collection, column] = field.split('.');
      if (column && getSubTables(target?.entityType)[collection]) return t(`lender.changeLog.fields.${column}`, { defaultValue: column });

      // A field nested deeper than the grid shows, as `homeAddress.city`, is named part by part.
      return field
        .split('.')
        .map(part => t(`lender.changeLog.fields.${part}`, { defaultValue: part }))
        .join(': ');
    },
    [findColumn, target, t],
  );

  // The stored code is what the diff compares, so the label has to be looked up here, from the same
  // value list the change request screen renders that column with. A list is labelled item by item.
  // A field that holds another record's id is named by the list the log reads for it.
  const valueLabel = useCallback(
    (field: string, value: ChangeLogValue): string | null => {
      if (value === null) return null;

      const options = findColumn(field)?.options;
      const listName = referenceListNames[field] ?? options?.listName;
      const list = listName ? (referenceLists[listName] ?? sharedValueLists[listName]) : undefined;
      const format = options?.format?.type;
      const label = (item: string) => {
        if (list?.[item]) return list[item];
        if (format === 'boolean') return t(item === 'true' ? 'enso.labels.yes' : 'enso.labels.no');
        if (format === 'percentage') return `${item}%`;
        // A date column holds UTC midnight, so its date is read as written rather than shifted to local time.
        if (DATE_TIME.test(item)) return format === 'date' ? item.slice(0, 10) : dayjs(item).format('YYYY-MM-DD HH:mm');

        return item;
      };

      return Array.isArray(value) ? value.map(label).join(', ') : label(value);
    },
    [findColumn, referenceListNames, referenceLists, sharedValueLists, t],
  );

  const emptyLabel = t('lender.changeLog.labels.emptyValue');
  const expandedColumns = useMemo(() => getExpandedColumns(emptyLabel, columnLabel, valueLabel), [emptyLabel, columnLabel, valueLabel]);

  // The Field filter's options are every field the log holds, ordered so that the record's own come
  // first and each collection's stay together: enso's select filter renders a flat list, so the order
  // and the collection's name in the label are what group them.
  const valueLists: ValueLists = useMemo(() => {
    const held = new Set(entries.flatMap(entry => entry.fields));
    const subTables = getSubTables(target?.entityType);
    const ordered = [
      ...entityColumns.map(column => String(column.key)),
      ...Object.entries(subTables).flatMap(([field, subTable]) => subTable.columns.map(column => `${field}.${String(column.key)}`)),
    ].filter(field => held.has(field));
    const rest = [...held].filter(field => !ordered.includes(field));

    return {
      ...sharedValueLists,
      [FIELD_LIST_NAME]: Object.fromEntries([...ordered, ...rest].map(field => [field, columnLabel(field)])),
      // Only the origins the log actually holds, so the filter never offers one that matches nothing.
      [ORIGIN_LIST_NAME]: Object.fromEntries(
        [...new Set(entries.map(entry => entry.origin.key))].map(origin => [origin, originList[origin] ?? origin]),
      ),
    };
  }, [entries, originList, columnLabel, sharedValueLists, entityColumns, target]);

  // A tag cell renders its `value`, so without this the badge shows the raw origin code.
  const rows = useMemo(
    () => entries.map(entry => ({ ...entry, origin: { ...entry.origin, value: originList[entry.origin.key] ?? entry.origin.key } })),
    [entries, originList],
  );

  // The Fields filter offers the record's own fields and then every collection's, so it groups them
  // under the same headings the detail below uses.
  const filterGroups = useMemo(() => {
    const held = new Set(entries.flatMap(entry => entry.fields));
    const subTables = getSubTables(target?.entityType);

    return Object.entries(subTables)
      .map(([field, subTable]) => {
        const columnKeys = subTable.columns.map(column => `${field}.${String(column.key)}`);
        const rest = [...held].filter(key => key.startsWith(`${field}.`) && !columnKeys.includes(key));

        return { label: t(subTable.title), keys: [...columnKeys.filter(key => held.has(key)), ...rest] };
      })
      .filter(group => group.keys.length);
  }, [entries, target, t]);

  // A deep link is the one cell no built-in renderer covers. Change requests only exist for Portal
  // changes, so every other row says so instead of showing an empty cell. The link carries the change
  // request id in the table's own search param, which is the contract the change request list's own
  // Copy link uses and the only one that list reads: nothing there looks at an `actionId` query
  // parameter.
  const renderChangeRequest = useCallback(
    (_: unknown, entry: ChangeLogEntry) =>
      entry.changeRequestId ? (
        <Link to={`/change-requests?search-change-requests=${encodeURIComponent(entry.changeRequestId)}`}>
          {t('lender.changeLog.labels.viewChangeRequest')}
        </Link>
      ) : (
        <Text type="secondary">{t('lender.changeLog.labels.autoApplied')}</Text>
      ),
    [t],
  );

  const tableColumns = useMemo(
    () =>
      columns.map(column => {
        if (column.key === 'fields') return { ...column, options: { ...column.options, filterGroups } };
        if (column.key === 'changeRequestId') return { ...column, render: renderChangeRequest };

        return column;
      }),
    [renderChangeRequest, filterGroups],
  );

  // The detail is fetched when a row opens, so the sub-table cannot come from enso's `expanded`: that
  // renders a nested array the row already carries. `expanded` is still declared below, because it is
  // what tells the Table it has expandable rows and brings the styling that keeps a sub-table inset and
  // framed instead of floating in the row.
  //
  // A collection gets a table of its own rather than more rows in the first one: its fields are the
  // change request screen's columns, and a row of it only means anything read across those columns.
  const expandedRowRender = useCallback(
    (record: ChangeLogEntry) => {
      const loaded = detail[record.id];
      const subTables = getSubTables(target?.entityType);
      const recordTitle = RECORD_SECTION_TITLES[target?.entityType as ActionEntityType];
      const sections = loaded?.collections ?? [];

      return (
        <div className={styles.detail}>
          {loaded && !loaded.changes.length && !sections.length && (
            <Text type="secondary">
              {t(loaded.failed ? 'lender.changeLog.labels.detailFailed' : 'lender.changeLog.labels.noFieldChanges')}
            </Text>
          )}
          {(!loaded || loaded.changes.length > 0) && (
            <div className={styles.section}>
              {!!sections.length && recordTitle && <Text strong>{t(recordTitle)}</Text>}
              <Table
                id={`changeLog-${record.id}`}
                dataSource={loaded?.changes ?? []}
                columns={expandedColumns}
                rowKey={rowKey}
                size="small"
                pagination={false}
                loading={!loaded}
                showViews={false}
                manageColumns={false}
              />
            </div>
          )}

          {sections.map(collection => (
            <div key={collection.field} className={styles.section}>
              <Text strong>{t(subTables[collection.field].title)}</Text>
              <Table
                id={`changeLog-${record.id}-${collection.field}`}
                dataSource={collection.changes}
                columns={getExpandedColumns(emptyLabel, columnLabel, valueLabel, {
                  title: columnLabel(`${collection.field}.${subTables[collection.field].rowLabel}`),
                  field: `${collection.field}.${subTables[collection.field].rowLabel}`,
                })}
                rowKey={rowKey}
                size="small"
                pagination={false}
                showViews={false}
                manageColumns={false}
              />
            </div>
          ))}
        </div>
      );
    },
    [detail, expandedColumns, target, t, emptyLabel, columnLabel, valueLabel],
  );

  const resetFilters = useCallback(() => {
    tableRef.current?.resetFilters();
    setFiltersActive(false);
  }, []);

  const expandable = useMemo(
    () => ({
      expandedRowRender,
      onExpand: (isExpanded: boolean, record: ChangeLogEntry) => {
        if (isExpanded) loadDetail(record.id, record.entityType);
      },
    }),
    [expandedRowRender, loadDetail],
  );

  const tableProps: TableProps<string, ChangeLogEntry, ChangeLogFieldChange> = useMemo(
    () => ({
      id: 'changeLog',
      dataSource: rows,
      columns: tableColumns,
      rowKey,
      expanded: { rowKey, columns: expandedColumns },
      expandable,
      pagination: true,
      loading: isFetching,
      // Without it a failed load would read as a record with no history.
      locale: failed ? { emptyText: t('lender.changeLog.labels.listFailed') } : undefined,
      valueLists,
      tagMappings: getOriginTagMappings(),
      showViews: false,
      manageColumns: false,
      onChange: (_pagination, filters) =>
        setFiltersActive(Object.values(filters).some(value => (Array.isArray(value) ? value.length > 0 : value != null))),
      scroll: { x: 'max-content', y: '100%' },
    }),
    [rows, tableColumns, expandable, expandedColumns, isFetching, failed, t, valueLists],
  );

  const crudProps: CrudProps<ChangeLogEntry> = useMemo(
    () => ({
      open: !!target,
      title: t('lender.changeLog.titleWithName', { name: target?.name ?? '' }),
      mode: CrudMode.VIEW,
      customForm: true,
      hideConfirm: true,
      // Wide enough for a collection's own columns to fit without the detail scrolling sideways.
      width: 1440,
      disableDrawerBodyScroll: true,
      labels: { cancel: t('enso.labels.close') },
      onClose,
      // Required by CrudProps, unreachable here: the drawer is read-only and hides the confirm button.
      onConfirm: async () => undefined,
    }),
    [target, onClose, t],
  );

  if (!target) return null;

  return (
    <Crud {...crudProps}>
      <div className={styles.drawerBody}>
        <Table {...tableProps} ref={attachTable}>
          <Table.ActionsBar>
            <Flex justify="space-between">
              <Space size="middle">
                <Table.GlobalSearch />
              </Space>
              <Space size="middle">
                {/* Not Table.ResetFiltersButton: its label covers sorting too, and this table has none. */}
                <Button icon={<StopOutlined />} disabled={!filtersActive} onClick={resetFilters}>
                  {t('lender.changeLog.labels.resetFilters')}
                </Button>
              </Space>
            </Flex>
          </Table.ActionsBar>
        </Table>
      </div>
    </Crud>
  );
});
